/**
 * Open reading frame (ORF) finder. Default uses the standard
 * genetic code (NCBI table 1). Returns all ORFs above a minimum
 * length, in all 6 reading frames.
 */

import { reverseComplement } from './fasta.js';

export interface Orf {
  /** 0-based start position (inclusive). */
  start: number;
  /** 0-based end position (exclusive). */
  end: number;
  /** +1 / +2 / +3 / -1 / -2 / -3. Negative = reverse strand. */
  frame: 1 | 2 | 3 | -1 | -2 | -3;
  /** Length in nucleotides (multiple of 3). */
  length: number;
  /** Translated protein (single-letter codes; * marks stop). */
  protein: string;
}

/** Standard NCBI table-1 genetic code. */
const CODON_TABLE: Record<string, string> = (() => {
  const aminos = 'FFLLSSSSYY**CC*WLLLLPPPPHHQQRRRRIIIMTTTTNNKKSSRRVVVVAAAADDEEGGGG';
  const bases = ['T', 'C', 'A', 'G'];
  const out: Record<string, string> = {};
  let idx = 0;
  for (const b1 of bases) for (const b2 of bases) for (const b3 of bases) {
    out[b1 + b2 + b3] = aminos[idx++]!;
  }
  return out;
})();

export function translateCodon(codon: string): string {
  return CODON_TABLE[codon.toUpperCase()] ?? 'X';
}

export function translate(seq: string): string {
  let out = '';
  for (let i = 0; i + 2 < seq.length; i += 3) {
    out += translateCodon(seq.slice(i, i + 3));
  }
  return out;
}

/**
 * Find ORFs above `minProteinLength` in all 6 reading frames.
 * An ORF starts at an ATG and ends at the next in-frame stop (TAA/TAG/TGA)
 * or at the sequence end.
 */
export function findOrfs(sequence: string, minProteinLength = 30): Orf[] {
  const seq = sequence.toUpperCase();
  const orfs: Orf[] = [];
  for (const strand of ['+', '-'] as const) {
    const s = strand === '+' ? seq : reverseComplement(seq);
    for (const frameOffset of [0, 1, 2]) {
      let startNt: number | null = null;
      let prot = '';
      for (let i = frameOffset; i + 2 < s.length; i += 3) {
        const codon = s.slice(i, i + 3);
        const aa = translateCodon(codon);
        if (startNt === null) {
          if (codon === 'ATG') {
            startNt = i;
            prot = 'M';
          }
        } else {
          if (aa === '*') {
            if (prot.length >= minProteinLength) {
              const startOnPlus = strand === '+' ? startNt : s.length - i;
              const endOnPlus = strand === '+' ? i : s.length - startNt;
              orfs.push({
                start: Math.min(startOnPlus, endOnPlus),
                end: Math.max(startOnPlus, endOnPlus),
                frame: ((strand === '+' ? 1 : -1) * (frameOffset + 1)) as Orf['frame'],
                length: prot.length * 3,
                protein: prot,
              });
            }
            startNt = null;
            prot = '';
          } else {
            prot += aa;
          }
        }
      }
      // End of sequence without stop: include if minimum.
      if (startNt !== null && prot.length >= minProteinLength) {
        const startOnPlus = strand === '+' ? startNt : s.length - s.length;
        const endOnPlus = strand === '+' ? s.length : s.length - startNt;
        orfs.push({
          start: Math.min(startOnPlus, endOnPlus),
          end: Math.max(startOnPlus, endOnPlus),
          frame: ((strand === '+' ? 1 : -1) * (frameOffset + 1)) as Orf['frame'],
          length: prot.length * 3,
          protein: prot,
        });
      }
    }
  }
  // Sort by start position.
  orfs.sort((a, b) => a.start - b.start);
  return orfs;
}
